import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import axios from "axios";
import { FileText, ClipboardList, ShieldCheck, PhoneCall, Mail, Globe, IndianRupee, CreditCard, QrCode, UploadCloud, Check, Paperclip, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KhataCard } from "@/components/portal/KhataCard";
import { PaymentHistory } from "@/components/portal/PaymentHistory";
import { NotificationBell } from "@/components/portal/NotificationBell";
import AdSlider from "@/components/ads/AdSlider";

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}
function inr(n) { return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`; }

function buildUpiIntent(upi, company, amount, ref) {
  if (!upi) return "";
  const parts = { pa: upi, pn: company || "Payee", am: Number(amount || 0).toFixed(2), cu: "INR", tn: `Invoice ${ref || ""}`.slice(0, 80) };
  return "upi://pay?" + Object.entries(parts).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

export default function Portal({ preloaded, onSignOut } = {}) {
  const params = useParams();
  const token = preloaded ? (preloaded.client?.portal_token || "") : params.token;
  const [d, setD] = useState(preloaded || null);
  const [err, setErr] = useState("");
  const [payingId, setPayingId] = useState(null);
  const [qrFor, setQrFor] = useState(null); // invoice being scanned
  const publicApi = React.useMemo(() => axios.create({ baseURL: API_BASE }), []);

  const load = () => {
    if (preloaded) {
      // authenticated client mode — refresh via the auth-gated endpoint
      import("@/lib/api").then(({ default: api }) => {
        api.get("/client/portal").then(r => setD(r.data)).catch(e => setErr(e.message));
      });
      return;
    }
    publicApi.get(`/portal/${token}`)
      .then(r => setD(r.data))
      .catch(e => setErr(e.response?.status === 404 ? "This portal link is invalid or has been revoked." : (e.message || "Something went wrong.")));
  };
  useEffect(() => { if (!preloaded) load(); }, [token]);

  const ensureRazorpay = async () => {
    if (window.Razorpay) return true;
    await new Promise((res, rej) => {
      const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existing) { existing.addEventListener("load", res); existing.addEventListener("error", rej); return; }
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = res; s.onerror = rej;
      document.body.appendChild(s);
    }).catch(() => {});
    return !!window.Razorpay;
  };

  const payInvoice = async (inv) => {
    if (!(await ensureRazorpay())) { toast.error("Payment library failed to load"); return; }
    setPayingId(inv.id);
    try {
      const { data } = await publicApi.post(`/portal/${token}/invoice/${inv.id}/order`, {});
      const opts = {
        key: data.key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: data.company_name || "Payment",
        description: data.description,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: data.theme_color || "#09090B" },
        handler: async (resp) => {
          try {
            await publicApi.post(`/portal/${token}/invoice/${inv.id}/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received", { description: `${data.invoice_number} · ₹${inv.total.toLocaleString("en-IN")}` });
            load();
          } catch (e) {
            toast.error("Verification failed", { description: e.response?.data?.detail || e.message });
          } finally { setPayingId(null); }
        },
        modal: { ondismiss: () => setPayingId(null) },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (r) => { toast.error("Payment failed", { description: r?.error?.description || "Try again" }); setPayingId(null); });
      rzp.open();
    } catch (e) {
      toast.error("Could not start payment", { description: e.response?.data?.detail || e.message });
      setPayingId(null);
    }
  };

  if (err) return (
    <div className="min-h-screen bg-zinc-100 grid place-items-center p-8">
      <div className="bg-white border border-zinc-200 max-w-md w-full p-8 text-center">
        <div className="text-[11px] tracking-[0.2em] uppercase text-rose-700 font-semibold">Access denied</div>
        <h1 className="font-heading font-black text-2xl mt-2">Portal unavailable</h1>
        <p className="text-sm text-zinc-600 mt-3">{err}</p>
        <p className="text-xs text-zinc-500 mt-6">Please ask your accountant to send you a fresh link.</p>
      </div>
    </div>
  );
  if (!d) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm tracking-[0.2em] uppercase">Loading portal…</div>;

  const { client, company, invoices, open_tasks, totals, due_dates = [] } = d;
  const accent = company.primary_color || "#09090B";

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {company.logo_url ? (
              <img src={absUrl(company.logo_url)} alt="logo" className="h-10 max-w-[120px] object-contain" />
            ) : (
              <div className="w-10 h-10 shrink-0 grid place-items-center text-white font-heading font-black text-lg" style={{ background: accent }}>
                {(company.name || "S")[0]}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold text-[15px] tracking-tight leading-none truncate">{company.name}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">Client Portal</div>
            </div>
            {onSignOut ? <span className="ml-auto md:ml-0"><NotificationBell /></span> : null}
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto no-scrollbar [&>a]:whitespace-nowrap [&>a]:shrink-0 [&>button]:shrink-0 [&>span]:shrink-0" data-testid="portal-nav">
            <div className="text-right text-xs text-zinc-500 hidden md:block">
              <div className="tabular">{company.phone}</div>
              <div>{company.email}</div>
            </div>
            {onSignOut ? (
              <a data-testid="portal-business-link" href="/my-business" className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 bg-zinc-900 text-white hover:bg-zinc-700 transition-colors mr-2">My Business</a>
            ) : null}
            {onSignOut ? (
              <a
                data-testid="portal-billing-link"
                href="/my-billing"
                className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors mr-2"
              >
                My Billing
              </a>
            ) : null}
            {onSignOut ? (
              <a
                data-testid="portal-inventory-link"
                href="/my-inventory"
                className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors mr-2"
              >
                My Inventory
              </a>
            ) : null}
            {onSignOut ? (
              <a data-testid="portal-pos-link" href="/pos" className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors mr-2">POS</a>
            ) : null}
            {onSignOut ? (
              <a data-testid="portal-purchases-link" href="/my-purchases" className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors mr-2">Purchases</a>
            ) : null}
            {onSignOut ? (
              <a data-testid="portal-reports-link" href="/my-reports" className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors mr-2">Reports</a>
            ) : null}
            {onSignOut ? (
              <a
                data-testid="portal-khata-link"
                href="/my-khata"
                className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors mr-2"
              >
                My Khata
              </a>
            ) : null}
            {onSignOut ? (
              <button
                data-testid="portal-signout-btn"
                onClick={onSignOut}
                className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors"
              >
                Sign out
              </button>
            ) : null}
          </div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5"><AdSlider placement="portal" /></div>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Portal for</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl mt-2 tracking-tight">{client.name}</h1>
        <p className="text-sm text-zinc-600 mt-3 max-w-xl">
          Your invoices and any documents we still need to complete your compliance work — all in one place. This page is private to your account{onSignOut ? "." : ". No login required."}
        </p>
      </section>

      {/* KPIs */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Invoices" value={totals.invoice_count} tone="default" testid="kpi-invoices" />
        <Kpi label="Pending amount" value={inr(totals.pending_amount)} tone="dark" testid="kpi-pending-amount" />
        <Kpi label="Paid tasks" value={totals.paid_task_count} tone="good" testid="kpi-paid-tasks" />
        <Kpi label="Docs pending" value={totals.docs_pending_count} tone={totals.docs_pending_count > 0 ? "warn" : "default"} testid="kpi-docs-pending" />
      </section>

      {/* Account / khata with the firm */}
      <KhataCard token={token} accent={accent} />
      <PaymentHistory token={token} />

      {/* Invoices */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-10">
        <SectionHead icon={FileText} title="Invoices" caption="Use Open PDF to download your invoice" />
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200">
                {["#", "Issued", "Due", "Total", "GST", "Status", ""].map(h => (
                  <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="stripe-rows">
              {invoices.map(i => (
                <tr key={i.id} data-testid={`portal-invoice-${i.id}`} className="border-t border-zinc-100">
                  <td className="px-3 py-2.5 font-mono text-xs">{i.invoice_number}</td>
                  <td className="px-3 py-2.5 tabular text-xs">{i.issue_date}</td>
                  <td className="px-3 py-2.5 tabular text-xs">{i.due_date}</td>
                  <td className="px-3 py-2.5 tabular font-semibold">{inr(i.total)}</td>
                  <td className="px-3 py-2.5 text-xs">{i.gst_enabled ? "Yes" : "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold ${i.status === "paid" ? "bg-emerald-100 text-emerald-800" : i.status === "sent" ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-800"}`}>{i.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {i.status !== "paid" ? (
                        <>
                          <button
                            data-testid={`portal-upi-qr-${i.id}`}
                            onClick={() => setQrFor(i)}
                            title="Pay with UPI QR"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold border rounded-none"
                            style={{ borderColor: accent, color: accent }}
                          >
                            <QrCode size={13} /> UPI QR
                          </button>
                          <button
                            data-testid={`portal-pay-${i.id}`}
                            onClick={() => payInvoice(i)}
                            disabled={payingId === i.id}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-white rounded-none disabled:opacity-60"
                            style={{ background: accent }}
                          >
                            <CreditCard size={13} />
                            {payingId === i.id ? "Opening…" : `Pay ${inr(i.total)}`}
                          </button>
                        </>
                      ) : null}
                      <a data-testid={`portal-pdf-${i.id}`} href={absUrl(i.pdf_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: accent }}>
                        Open PDF →
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
              {!invoices.length && <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Upcoming statutory due dates */}
      {due_dates.length ? (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-10" data-testid="portal-due-dates">
          <SectionHead icon={CalendarDays} title="Your upcoming due dates" caption="Statutory filing deadlines for your registrations and the documents we'll need from you" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {due_dates.map((dd, i) => {
              const urgent = dd.days_left <= 7;
              const pending = dd.documents_pending ?? dd.documents;
              return (
                <article key={`${dd.form}-${dd.due_date}`} data-testid={`portal-due-${i}`} className={`border bg-white p-4 ${urgent ? "border-rose-300" : "border-zinc-200"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-sm truncate">{dd.form}</div>
                      <div className="text-[11px] text-zinc-500">{dd.period} · {dd.desc}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-bold text-sm tabular">{new Date(dd.due_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                      <div className={`text-[10px] font-semibold tabular ${urgent ? "text-rose-700" : "text-zinc-500"}`}>
                        {dd.days_left === 0 ? "Due today" : `${dd.days_left} day${dd.days_left === 1 ? "" : "s"} left`}
                      </div>
                    </div>
                  </div>
                  {dd.task_status ? (
                    <div className="mt-2 text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500">Status with us: <span className="text-zinc-900">{dd.task_status}</span></div>
                  ) : null}
                  {pending && pending.length ? (
                    <div className="mt-3 border-t border-zinc-100 pt-3">
                      <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-amber-700">Please share</div>
                      <ul className="mt-2 space-y-1.5">
                        {pending.map(doc => (
                          <li key={doc} className="text-xs text-zinc-700 flex items-center gap-2">
                            <span className="w-1 h-1 bg-amber-500 shrink-0" />
                            <span className="flex-1">{doc}</span>
                            {dd.task_id ? <PortalDocUploader token={token} taskId={dd.task_id} docName={doc} onDone={load} /> : null}
                          </li>
                        ))}
                      </ul>
                      {!dd.task_id ? <div className="mt-2 text-[10px] text-zinc-400">Upload becomes available once we open this filing for you.</div> : null}
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1"><ShieldCheck size={13} /> All documents received</div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Docs pending / open tasks */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-10">
        <SectionHead icon={ClipboardList} title="Work in progress & documents we need" caption="Please share the pending items so we can complete your filings on time" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {open_tasks.map(t => (
            <article key={t.id} data-testid={`portal-task-${t.id}`} className="border border-zinc-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{t.service}</div>
                  <div className="text-[11px] text-zinc-500">{t.description}</div>
                </div>
                <span className={`text-[10px] font-bold tracking-widest px-2 py-0.5 shrink-0 ${t.priority === "High" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`}>{t.priority}</span>
              </div>
              <div className="mt-2 text-[11px] text-zinc-500 tabular">Deadline: {(t.deadline || "").slice(0,10)}</div>
              {(t.documents_pending || []).length > 0 ? (
                <div className="mt-3 border-t border-zinc-100 pt-3">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-amber-700">Documents pending</div>
                  <ul className="mt-2 space-y-1.5">
                    {(t.documents_pending || []).map(doc => (
                      <li key={doc} className="text-xs text-zinc-700 flex items-center gap-2">
                        <span className="w-1 h-1 bg-amber-500 shrink-0" />
                        <span className="flex-1">{doc}</span>
                        <PortalDocUploader token={token} taskId={t.id} docName={doc} onDone={load} />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1"><ShieldCheck size={13} /> All documents received</div>
              )}
              {/* Uploaded files list */}
              {(t.uploaded_files || []).length > 0 ? (
                <div className="mt-3 border-t border-zinc-100 pt-3">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-emerald-700 flex items-center gap-1">
                    <Check size={11} /> Uploaded by you
                  </div>
                  <ul className="mt-1 space-y-0.5" data-testid={`portal-uploaded-${t.id}`}>
                    {(t.uploaded_files || []).map(f => (
                      <li key={f.id} className="text-[11px] text-zinc-600 flex items-center gap-1.5 truncate">
                        <Paperclip size={10} className="shrink-0" />
                        <span className="truncate flex-1">{f.original_filename}</span>
                        {f.portal_doc_name ? (
                          <span className="text-[9px] tracking-widest uppercase text-emerald-700 shrink-0">{f.portal_doc_name}</span>
                        ) : null}
                        <span className="text-[9px] text-zinc-400 tabular shrink-0">{(f.created_at || "").slice(0,10)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {/* Extra document uploader — always visible */}
              <div className="mt-3 border-t border-zinc-100 pt-3">
                <PortalDocUploader token={token} taskId={t.id} docName={null} onDone={load} variant="button" />
              </div>
            </article>
          ))}
          {!open_tasks.length && <div className="border border-zinc-200 bg-white p-6 text-sm text-zinc-500 md:col-span-2 text-center">Nothing pending. We&apos;re all caught up.</div>}
        </div>
      </section>

      {/* Pay via UPI + bank */}
      {(company.upi_id || company.bank_account) ? (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-10">
          <SectionHead icon={IndianRupee} title="Payment options" caption="Bank transfer, UPI or contact us for a Razorpay link" />
          <div className="border border-zinc-200 bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-zinc-500">UPI</div>
              <div className="mt-1 font-mono tabular text-base">{company.upi_id || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-zinc-500">Bank transfer (NEFT/RTGS)</div>
              <div className="mt-1 leading-6 tabular">
                {company.bank_name}<br />
                A/c: {company.bank_account}<br />
                IFSC: {company.bank_ifsc}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <footer className="max-w-5xl mx-auto px-4 sm:px-6 py-12 mt-8 text-xs text-zinc-500">
        <div className="border-t border-zinc-200 pt-6 flex flex-wrap gap-4 items-center justify-between">
          <div>© {new Date().getFullYear()} {company.name}. GSTIN {company.gstin}.</div>
          <div className="flex gap-4">
            {company.phone ? <span className="flex items-center gap-1"><PhoneCall size={12} /> {company.phone}</span> : null}
            {company.email ? <span className="flex items-center gap-1"><Mail size={12} /> {company.email}</span> : null}
            {company.website ? <a className="flex items-center gap-1 hover:underline" href={company.website} target="_blank" rel="noreferrer"><Globe size={12} /> Website</a> : null}
          </div>
        </div>
      </footer>

      <Dialog open={!!qrFor} onOpenChange={(v) => !v && setQrFor(null)}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">Scan &amp; Pay</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Open any UPI app (Google Pay, PhonePe, Paytm, BHIM) and scan this QR to pay {qrFor ? inr(qrFor.total) : ""}.
            </DialogDescription>
          </DialogHeader>
          {qrFor ? (
            <div className="flex flex-col items-center gap-3">
              <img
                data-testid="portal-upi-qr-img"
                src={`${API_BASE}/portal/${token}/invoice/${qrFor.id}/upi-qr`}
                alt="UPI QR"
                className="w-64 h-64 border border-zinc-200 bg-white"
                onError={() => toast.error("UPI QR unavailable", { description: "Ask us to add a UPI ID to settings." })}
              />
              <div className="text-[11px] text-zinc-500 tabular">
                To: <span className="font-mono">{d?.company?.upi_id || "—"}</span>
              </div>
              <div className="text-xs text-zinc-700">
                Reference: <span className="font-mono">{qrFor.invoice_number}</span>
              </div>

              {/* Deep-link for mobile users */}
              {d?.company?.upi_id ? (
                <a
                  data-testid="portal-upi-deeplink"
                  href={buildUpiIntent(d.company.upi_id, d.company.name, qrFor.total, qrFor.invoice_number)}
                  className="w-full mt-1 px-3 py-2 text-xs font-semibold text-center border rounded-none"
                  style={{ borderColor: accent, color: accent }}
                >
                  Pay via UPI app · {inr(qrFor.total)}
                </a>
              ) : null}

              <div className="grid grid-cols-2 gap-2 w-full">
                <a
                  data-testid="portal-upi-save"
                  href={`${API_BASE}/portal/${token}/invoice/${qrFor.id}/upi-qr`}
                  download={`upi-${qrFor.invoice_number}.png`}
                  className="px-3 py-1.5 text-xs font-semibold text-center border border-zinc-300 rounded-none hover:bg-zinc-50"
                >
                  Save QR
                </a>
                <button
                  data-testid="portal-upi-close"
                  onClick={() => setQrFor(null)}
                  className="px-3 py-1.5 text-xs font-semibold text-white rounded-none"
                  style={{ background: accent }}
                >
                  Done
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionHead({ icon: Icon, title, caption }) {
  return (
    <header className="mb-3">
      <div className="flex items-center gap-2">
        <Icon size={16} strokeWidth={1.75} />
        <h2 className="font-heading font-bold tracking-tight text-xl">{title}</h2>
      </div>
      {caption ? <p className="text-xs text-zinc-500 mt-1 ml-6">{caption}</p> : null}
    </header>
  );
}

function Kpi({ label, value, tone = "default", testid }) {
  const tones = {
    default: "bg-white border-zinc-200 text-zinc-900",
    dark: "bg-zinc-900 border-zinc-900 text-white",
    good: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
  }[tone];
  return (
    <div className={`border p-4 ${tones}`} data-testid={testid}>
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold opacity-80">{label}</div>
      <div className="font-heading font-black text-2xl md:text-3xl tabular mt-2">{value}</div>
    </div>
  );
}


function PortalDocUploader({ token, taskId, docName, onDone, variant = "chip" }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const publicApi = React.useMemo(() => axios.create({ baseURL: API_BASE }), []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = docName ? `?doc_name=${encodeURIComponent(docName)}` : "";
      await publicApi.post(`/portal/${token}/task/${taskId}/upload${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded", { description: docName ? `${docName} received` : file.name });
      onDone?.();
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const accept = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.xlsx,.xls,.docx,.doc,.txt,.zip";
  const testid = docName ? `portal-upload-${taskId}-${docName.replace(/\s+/g, "-").toLowerCase()}` : `portal-upload-other-${taskId}`;

  return (
    <>
      <input
        ref={inputRef}
        data-testid={`${testid}-input`}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {variant === "chip" ? (
        <button
          data-testid={testid}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="text-[10px] tracking-widest uppercase font-semibold text-zinc-900 bg-white border border-zinc-300 px-2 py-0.5 hover:bg-zinc-900 hover:text-white disabled:opacity-50"
        >
          {busy ? "…" : "Upload"}
        </button>
      ) : (
        <button
          data-testid={testid}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full flex items-center justify-center gap-1.5 text-[11px] tracking-widest uppercase font-semibold border border-dashed border-zinc-300 py-2 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 disabled:opacity-50"
        >
          <UploadCloud size={12} />
          {busy ? "Uploading…" : "Upload another document"}
        </button>
      )}
    </>
  );
}
